import React, { useEffect, useMemo, useState } from "react";
import { Table, Card, Typography, Drawer, Button, Row, Col } from "antd";
import { FileExcelOutlined } from "@ant-design/icons";
import axiosInstance from "../../configs";
import { getUser } from "../../utils/DataUser";
import dayjs from "dayjs";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const { Title, Text } = Typography;

// === KOM PONEN UNTUK MENAMPILKAN HISTORY SATU NOPEN ===
function DocHistory({ doc }) {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axiosInstance.get("/monitoring-stock/history", {
          params: {
            id_customer: doc.id_customer,
            item_code: doc.item_code,
            nopen: doc.nopen,
            seri: doc.seri,
          },
        });
        setRows(res.data || []);
      } catch (e) {
        console.error("load history error", e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [doc]);

  const columns = [
    {
      title: "Tanggal",
      dataIndex: "created_at",
      key: "created_at",
      render: (value) => (value ? new Date(value).toLocaleString("id-ID") : ""),
      width: 180,
    },
    {
      title: "Jenis Kegiatan",
      dataIndex: "type_label",
      key: "type_label",
      width: 150,
    },
    {
      title: "No. Kegiatan",
      dataIndex: "no_kegiatan",
      key: "no_kegiatan",
      width: 140,
    },
    {
      title: "Lot",
      dataIndex: "lokasi_lot",
      key: "lokasi_lot",
      width: 80,
    },
    {
      title: "Jumlah (Kemasan)",
      dataIndex: "jumlah_kemasan",
      key: "jumlah_kemasan",
      width: 140,
      align: "right",
      render: (val) => (val != null ? Number(val).toLocaleString("id-ID") : ""),
    },
    {
      title: "Catatan",
      dataIndex: "catatan",
      key: "catatan",
    },
  ];

  return (
    <Table
      columns={columns}
      dataSource={rows}
      loading={loading}
      pagination={false}
      rowKey={(r) => r.id_item_stock_history}
      size="small"
    />
  );
}

export default function MonitoringStock() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);

  // Check user level
  const user = getUser();
  const canViewSummary =
    user &&
    user.id_level !== 8 &&
    user.id_user_level !== 8 &&
    user.id_level !== "8" &&
    user.id_user_level !== "8";
  const canViewDetail =
    user &&
    (user.id_level === 8 ||
      user.id_user_level === 8 ||
      user.id_level === "8" ||
      user.id_user_level === "8" ||
      user.id_level === 1 ||
      user.id_user_level === 1 ||
      user.id_level === "1" ||
      user.id_user_level === "1");

  // Drawer state
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);

  const onNopenClick = (doc) => {
    setSelectedDoc(doc);
    setDrawerVisible(true);
  };

  const loadData = async () => {
    try {
      const user = getUser();
      const params = {};

      // Filter di backend untuk user level 7 (Importir)
      if (
        user &&
        (user.id_level === 7 ||
          user.id_user_level === 7 ||
          user.id_level === "7" ||
          user.id_user_level === "7")
      ) {
        params.id_customer = user.username; // username = NPWP
      }

      const res = await axiosInstance.get("/monitoring-stock", { params });
      const fetchedData = res.data || [];
      setData(fetchedData);
    } catch (e) {
      console.error("load monitoring stock error", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Level 3: detail per dokumen / nopen + lot
  const renderDocTable = (itemRecord, customerRecord) => {
    const columns = [
      {
        title: "Nopen",
        dataIndex: "nopen",
        key: "nopen",
        width: 160,
        render: (text, record) => (
          <Button
            type="link"
            style={{
              color: "#1890ff",
              cursor: "pointer",
              fontWeight: "bold",
              padding: 0,
            }}
            onClick={() => onNopenClick(record)}
          >
            {text}
          </Button>
        ),
      },
      {
        title: "Tanggal Nopen",
        dataIndex: "tanggal_nopen",
        key: "tanggal_nopen",
        width: 110,
        render: (value) => (value ? dayjs(value).format("DD-MM-YYYY") : ""),
      },
      {
        title: "Seri",
        dataIndex: "seri",
        key: "seri",
        width: 80,
      },
      {
        title: "Masa Timbun (hari)",
        dataIndex: "masa_timbun",
        key: "masa_timbun",
        width: 120,
      },
      {
        title: "Lokasi Lot",
        dataIndex: "lokasi_lot",
        key: "lokasi_lot",
        width: 100,
      },
      {
        title: "PCE",
        dataIndex: "total_pce",
        key: "total_pce",
        width: 120,
        align: "right",
        render: (val) =>
          val != null ? Number(val).toLocaleString("id-ID") : "",
      },
      {
        title: "CT",
        dataIndex: "total_ct",
        key: "total_ct",
        width: 120,
        align: "right",
        render: (val) =>
          val != null ? Number(val).toLocaleString("id-ID") : "",
      },
    ];

    const dataSource =
      (itemRecord.docs || []).map((d) => ({
        ...d,
        id_customer: d.id_customer || customerRecord.id_customer,
        item_code: d.item_code || itemRecord.item_code,
      })) || [];

    return (
      <Table
        columns={columns}
        dataSource={dataSource}
        pagination={false}
        rowKey={(row) =>
          `${row.id_customer}-${row.item_code}-${row.nopen}-${row.seri}-${row.lokasi_lot}`
        }
        size="small"
        expandable={{
          expandedRowRender: (docRow) => <DocHistory doc={docRow} />,
          rowExpandable: () => true,
        }}
        // === WARNA ROW BERDASARKAN MASA TIMBUN ===
        rowClassName={(record) => {
          if (record.masa_timbun > 3 * 365) return "masa-timbun-merah";
          if (record.masa_timbun > 365) return "masa-timbun-kuning";
          return "";
        }}
      />
    );
  };

  // Level 2: table item per importir (bisa expand ke level 3)
  const renderItemTable = (customerRecord) => {
    const columns = [
      {
        title: "Nama Barang",
        dataIndex: "item_name",
        key: "item_name",
        width: 320,
      },
      {
        title: "PCE",
        dataIndex: "total_pce",
        key: "total_pce",
        width: 120,
        align: "right",
        render: (val) =>
          val != null ? Number(val).toLocaleString("id-ID") : "",
      },
      {
        title: "CT",
        dataIndex: "total_ct",
        key: "total_ct",
        width: 120,
        align: "right",
        render: (val) =>
          val != null ? Number(val).toLocaleString("id-ID") : "",
      },
    ];

    return (
      <Table
        columns={columns}
        dataSource={customerRecord.items || []}
        pagination={false}
        rowKey={(item) => item.item_code}
        size="small"
        expandable={{
          expandedRowRender: (itemRecord) =>
            renderDocTable(itemRecord, customerRecord),
          rowExpandable: (item) =>
            Array.isArray(item.docs) && item.docs.length > 0,
        }}
      />
    );
  };

  // Level 1: per importir
  const columns = [
    {
      title: "Nama Importir",
      dataIndex: "nama_customer",
      key: "nama_customer",
      render: (text) => <b>{text}</b>,
    },
    {
      title: "Sum of PCE",
      dataIndex: "total_pce",
      key: "total_pce",
      width: 150,
      align: "right",
      render: (val) => (val != null ? Number(val).toLocaleString("id-ID") : ""),
    },
    {
      title: "Sum of CT",
      dataIndex: "total_ct",
      key: "total_ct",
      width: 150,
      align: "right",
      render: (val) => (val != null ? Number(val).toLocaleString("id-ID") : ""),
    },
  ];

  // Compute grand totals
  const grandTotalPCE = useMemo(() => {
    return data.reduce((sum, row) => sum + (Number(row.total_pce) || 0), 0);
  }, [data]);

  const grandTotalCT = useMemo(() => {
    return data.reduce((sum, row) => sum + (Number(row.total_ct) || 0), 0);
  }, [data]);

  // Export to Excel
  async function handleExportExcel(type) {
    let fetchedData = [];

    try {
      const user = getUser();
      const params = {};

      // Filter di backend untuk user level 7 (Importir)
      if (
        user &&
        (user.id_level === 7 ||
          user.id_user_level === 7 ||
          user.id_level === "7" ||
          user.id_user_level === "7")
      ) {
        params.id_customer = user.username; // username = NPWP
      }

      const res = await axiosInstance.get("/monitoring-stock/export", {
        params,
      });
      fetchedData = res.data || [];
    } catch (e) {
      console.error("load monitoring stock error", e);
      return;
    }

    const wb = XLSX.utils.book_new();

    // ============ SHEET 1: DASHBOARD (pivot-style with row grouping) ============
    const dashRows = [];
    const rowOutlines = []; // Track outline level per row
    const dashMerges = [];

    const isDetail = type !== "summary";
    const titleMergeColEnd = isDetail ? 3 : 2;

    // Row 0: Title
    dashRows.push(["PT. ATLANTIK LOGISTIK BERIKAT"]);
    rowOutlines.push({});
    dashMerges.push({ s: { r: 0, c: 0 }, e: { r: 0, c: titleMergeColEnd } });

    // Row 1: Date
    dashRows.push([
      `Stock Update : ${dayjs().format("DD MMMM YYYY").toUpperCase()}`,
    ]);
    rowOutlines.push({});
    dashMerges.push({ s: { r: 1, c: 0 }, e: { r: 1, c: titleMergeColEnd } });

    // Row 2: empty
    dashRows.push([]);
    rowOutlines.push({});

    // Row 3: Headers
    if (isDetail) {
      dashRows.push(["IMPORTIR", "", "OF", "ML", "SUM OF PCE", "SUM OF CT"]);
      rowOutlines.push({});
      dashMerges.push({ s: { r: 3, c: 0 }, e: { r: 3, c: 1 } });
    } else {
      dashRows.push(["IMPORTIR", "SUM OF PCE", "SUM OF CT"]);
      rowOutlines.push({});
    }

    let currentRowIndex = 4; // Data rows start at index 4

    // Data rows
    fetchedData.forEach((customer) => {
      const custPce = Number(customer.total_pce) || 0;
      const custCt = Number(customer.total_ct) || 0;

      if (type === "summary" && custPce <= 0 && custCt <= 0) {
        return;
      }

      // Importir summary row (parent — no outline level)
      if (isDetail) {
        dashRows.push([
          customer.nama_customer || "-",
          "",
          "",
          "",
          custPce,
          custCt,
        ]);
        rowOutlines.push({});
        dashMerges.push({
          s: { r: currentRowIndex, c: 0 },
          e: { r: currentRowIndex, c: 1 },
        });
      } else {
        dashRows.push([customer.nama_customer || "-", custPce, custCt]);
        rowOutlines.push({});
      }
      currentRowIndex++;

      // Item sub-rows (children — outline level 1, collapsible)
      if (Array.isArray(customer.items)) {
        customer.items.forEach((item) => {
          const itemPce = Number(item.total_pce) || 0;
          const itemCt = Number(item.total_ct) || 0;

          if (type === "summary" && itemPce <= 0 && itemCt <= 0) {
            return;
          }

          if (isDetail) {
            dashRows.push([
              `    ${item.item_code || "-"}`,
              item.item_name || "-",
              Number(item.isi_kemasan) || 0,
              Number(item.isi_per_unit) || 0,
              itemPce,
              itemCt,
            ]);
          } else {
            dashRows.push([
              `    ${item.item_name || item.item_code}`,
              itemPce,
              itemCt,
            ]);
          }
          rowOutlines.push({ level: 1 });
          currentRowIndex++;
        });
      }
    });

    // Grand Total
    if (isDetail) {
      dashRows.push(["GRAND TOTAL", "", grandTotalPCE, grandTotalCT]);
      rowOutlines.push({});
      dashMerges.push({
        s: { r: currentRowIndex, c: 0 },
        e: { r: currentRowIndex, c: 1 },
      });
    } else {
      dashRows.push(["GRAND TOTAL", grandTotalPCE, grandTotalCT]);
      rowOutlines.push({});
    }
    currentRowIndex++;

    const wsDash = XLSX.utils.aoa_to_sheet(dashRows);

    // Column widths
    if (isDetail) {
      wsDash["!cols"] = [{ wch: 25 }, { wch: 45 }, { wch: 18 }, { wch: 18 }];
    } else {
      wsDash["!cols"] = [{ wch: 45 }, { wch: 18 }, { wch: 18 }];
    }

    // Merge title and date cells across 4 columns
    wsDash["!merges"] = dashMerges;

    // Set row outline levels for collapsible grouping
    wsDash["!rows"] = rowOutlines;

    // Summary rows are ABOVE detail rows (default Excel expects below)
    wsDash["!outline"] = { above: true };

    XLSX.utils.book_append_sheet(wb, wsDash, "DASHBOARD");

    // ============ INDIVIDUAL IMPORTER SHEETS ============
    const usedSheetNames = new Set(["DASHBOARD"]);

    fetchedData.forEach((customer) => {
      const custPce = Number(customer.total_pce) || 0;
      const custCt = Number(customer.total_ct) || 0;

      if (custPce <= 0 && custCt <= 0) {
        return;
      }

      const sheetRows = [];

      // Row 0: empty row
      sheetRows.push([]);

      if (type === "summary") {
        // Row 1: Headers
        sheetRows.push(["NAMA BARANG", "SUM OF PCE", "SUM OF CT"]);

        // Data rows (Summary for this importer)
        if (Array.isArray(customer.items)) {
          customer.items.forEach((item) => {
            const itemPce = Number(item.total_pce) || 0;
            const itemCt = Number(item.total_ct) || 0;

            if (itemPce > 0 || itemCt > 0) {
              sheetRows.push([
                `${item.item_name || item.item_code}`,
                itemPce,
                itemCt,
              ]);
            }
          });
        }
      } else {
        // Row 1: Headers
        sheetRows.push([
          "NO",
          "JENIS",
          "NOPEN",
          "TANGGAL NOPEN",
          "TANGGAL MASUK",
          "KODE BARANG",
          "NAMA BARANG",
          "OF",
          "ML",
          "BO",
          "CT",
          "PENGIRIM",
        ]);

        let no = 1;

        // Item detail rows
        if (Array.isArray(customer.items)) {
          customer.items.forEach((item) => {
            if (Array.isArray(item.docs)) {
              item.docs.forEach((doc) => {
                const docPce = Number(doc.total_pce) || 0;
                const docCt = Number(doc.total_ct) || 0;

                if (docPce > 0 || docCt > 0) {
                  sheetRows.push([
                    no++,
                    `BC${doc.id_document || ""}`,
                    doc.nopen || "",
                    dayjs(doc.tanggal_nopen).format("DD-MM-YYYY") || "",
                    dayjs(doc.bpb_date).format("DD-MM-YYYY") || "",
                    doc.item_code || "",
                    item.item_name || "",
                    Number(doc.isi_kemasan) || 0,
                    Number(doc.isi_per_unit) || 0,
                    docPce,
                    docCt,
                    doc.id_contact || "",
                  ]);
                }
              });
            }
          });
        }
      }

      const wsImporter = XLSX.utils.aoa_to_sheet(sheetRows);

      if (type === "summary") {
        wsImporter["!cols"] = [{ wch: 45 }, { wch: 18 }, { wch: 18 }];
      } else {
        wsImporter["!cols"] = [
          { wch: 5 },
          { wch: 10 },
          { wch: 10 },
          { wch: 15 },
          { wch: 15 },
          { wch: 15 },
          { wch: 45 },
          { wch: 5 },
          { wch: 5 },
          { wch: 5 },
          { wch: 5 },
          { wch: 45 },
        ];
      }

      // Sanitize sheet name: remove invalid chars, truncate to 31 chars (Excel limit)
      let sheetName = (customer.nama_customer || "Unknown")
        .replace(/[\\\/\?\*\[\]:]/g, "")
        .trim()
        .substring(0, 31);

      // Ensure unique sheet names
      let finalName = sheetName;
      let counter = 1;
      while (usedSheetNames.has(finalName.toUpperCase())) {
        finalName = `${sheetName.substring(0, 28)}_${counter}`;
        counter++;
      }
      usedSheetNames.add(finalName.toUpperCase());

      XLSX.utils.book_append_sheet(wb, wsImporter, finalName);
    });

    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([buf], { type: "application/octet-stream" });
    const fileName =
      type === "summary"
        ? `Monitoring_Stock_Summary_${dayjs().format("YYYYMMDD")}.xlsx`
        : `Monitoring_Stock_Detail_${dayjs().format("YYYYMMDD")}.xlsx`;
    saveAs(blob, fileName);
  }

  return (
    <Card>
      <Row justify="space-between" align="middle" style={{ marginBottom: 0 }}>
        <Col>
          <Title level={3} style={{ marginBottom: 0 }}>
            PT. ATLANTIK LOGISTIK BERIKAT
          </Title>
          <Text type="secondary" style={{ fontSize: 14, display: "block" }}>
            Stock Update : {dayjs().format("DD MMMM YYYY").toUpperCase()}
          </Text>
        </Col>
        <Col style={{ display: "flex", gap: "8px" }}>
          {canViewSummary && (
            <Button
              type="primary"
              icon={<FileExcelOutlined />}
              onClick={() => handleExportExcel("summary")}
              style={{
                background: "#1E1EFA",
                borderColor: "#1E1EFA",
                fontWeight: 600,
              }}
            >
              Excel Summary
            </Button>
          )}
          {canViewDetail && (
            <Button
              type="primary"
              icon={<FileExcelOutlined />}
              onClick={() => handleExportExcel("detail")}
              style={{
                background: "#108ee9",
                borderColor: "#108ee9",
                fontWeight: 600,
              }}
            >
              Excel Detail
            </Button>
          )}
        </Col>
      </Row>

      <Table
        style={{ marginTop: 16 }}
        loading={loading}
        columns={columns}
        dataSource={data}
        rowKey={(record) => record.id_customer}
        pagination={false}
        expandable={{
          expandedRowRender: renderItemTable,
          rowExpandable: (record) =>
            Array.isArray(record.items) && record.items.length > 0,
        }}
      />

      {/* GRAND TOTAL row below table */}
      {!loading && data.length > 0 && (
        <div
          style={{
            display: "flex",
            background: "#E8F4FD",
            fontWeight: 700,
            fontSize: 14,
            padding: "12px 16px",
            borderBottom: "1px solid #f0f0f0",
            borderLeft: "1px solid #f0f0f0",
            borderRight: "1px solid #f0f0f0",
          }}
        >
          <div style={{ flex: 1, paddingLeft: 48 }}>GRAND TOTAL</div>
          <div style={{ width: 150, textAlign: "right" }}>
            {grandTotalPCE.toLocaleString("id-ID")}
          </div>
          <div style={{ width: 150, textAlign: "right" }}>
            {grandTotalCT.toLocaleString("id-ID")}
          </div>
        </div>
      )}

      <Drawer
        title={`History Nopen ${selectedDoc?.nopen || ""}`}
        width={800}
        placement="right"
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        destroyOnClose
      >
        {selectedDoc && <DocHistory doc={selectedDoc} />}
      </Drawer>
    </Card>
  );
}
